import {
  Box,
  Divider,
  Grid,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  RemoveRedEye,
  Settings,
  Delete,
} from '@mui/icons-material';
import {
  OnboardingStep,
  OnboardingStepStatus as OnboardingStepStatusType,
} from 'further-types/lp/onboarding';
import { Column } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import PageFilters, { Filter } from 'components/new/PageFilters';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Switch from 'further-ui/components/Switch';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { useLayoutContext } from 'contexts/LayoutContext';
import useOnboardingOverview from './useOnboardingOverview';
import useOnboardingInvestorSort from './useOnboardingInvestorSort';
import TargetValue from './TargetValue';
import CommitmentsValueGraph from './CommitmentsValueGraph';
import OnboardingStepStatus from './OnboardingStepStatus';
import CommitmentsDetails from './CommitmentsDetails';
import AddInvestorDrawer from 'components/new/AddInvestorDrawer';
import ViewInvestor from './ViewInvestor';
import { TabType } from './ViewInvestor/types';
import StatusBadge from 'components/new/StatusBadge';
import ErrorBoundary from 'components/ErrorBoundary';
import PageLoader from './ViewInvestor/PageLoader';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';
import { Vehicle } from 'further-types/lp/vehicle';
import OnboardingOverviewNavigationContextProvider, {
  useOnboardingOverviewNavigationContext,
} from './OnboardingOverviewNavigationContext';
import { CalendarTime } from 'further-ui/utils';

const pageId = 'onboarding-overview-page';

//TODO: Remove this once data room setup is implemented
const isDataRoomSetupCompleted = false;

const EmptyStatus = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xs),
}));

type Props = {
  vehicle: Vehicle & { createdAt?: CalendarTime };
};

const Overview: React.FC<Props> = ({ vehicle }) => {
  const theme = useTheme();

  const deleteCommitmentsDisclosure = useDisclosure<{ id: string }>();
  const removeInvestorDisclosure = useDisclosure<{ id: string }>();
  const addInvestorDisclosure = useDisclosure();

  const { onboardingSettings } = useOnboardingSettings(vehicle._id);
  const targetCloseDate = onboardingSettings.targetCloseDate ?? null;
  const fundraiseTarget = onboardingSettings.fundraiseTarget;

  const {
    filters,
    handleChangeFilters,
    pagination,
    commitmentStats,
    investors,
    totalInvestors,
    isFetching,
    isRefetching,
    signedCommitmentsValue,
    prospectiveCommitmentsValue,
    handleDeleteCommitment,
    handleRemoveInvestor,
    isRemovingInvestor,
    updateOnboardingSettings,
    isUpdatingOnboardingSettings,
    refetchInvestors,
    hasClosedCommitments,
  } = useOnboardingOverview(pageId, vehicle._id);
  const { sortSelectOptions, sortSelectValue, handleChangeSort } =
    useOnboardingInvestorSort(pagination);

  const {
    goToVehicleApplication,
    selectedVehicleApplicationId,
    closeVehicleApplication,
  } = useOnboardingOverviewNavigationContext();

  const handleSaveTargetValue = (value: number) => {
    updateOnboardingSettings({
      fundraiseTarget: value,
    });
  };

  const handleChangeTargetCloseDate = (value: CalendarDay | null) => {
    if (!vehicle._id) {
      return;
    }

    updateOnboardingSettings({
      targetCloseDate: value,
    });
  };

  const columns: Array<TableColumn<(typeof investors)[number]>> = [
    {
      label: 'Investor',
      key: 'investorName',
    },
    {
      label: 'Commitment',
      key: 'commitment',
      render: (investor) =>
        numberToCurrencyString(investor.commitmentAmount, {
          currency: vehicle.baseCurrency,
        }),
    },
    {
      label: 'Data room',
      key: 'dataRoom',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.DataRoomView}
          forceStatus={
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            !isDataRoomSetupCompleted
              ? OnboardingStepStatusType.Disabled
              : undefined
          }
        />
      ),
    },
    {
      label: 'Details',
      key: 'details',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.InvestorDetails}
        />
      ),
    },
    {
      label: 'Classification',
      key: 'classification',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.InvestorClassification}
        />
      ),
    },
    {
      label: 'Tax',
      key: 'tax',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.TaxDeclarations}
        />
      ),
    },
    {
      label: 'KYC/KYB',
      key: 'kycKyb',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.KYCOrKYB}
        />
      ),
    },
    {
      label: 'Signed',
      key: 'signed',
      render: (investor) => (
        <OnboardingStepStatus
          steps={investor.applicationSteps}
          currentStep={OnboardingStep.DocumentSigning}
        />
      ),
    },
    {
      label: 'Commitment made',
      key: 'commitmentMade',
      render: (investor) => {
        const tooltip = investor.hasCommitment
          ? 'This step has been completed.'
          : 'This step is pending investor completion.';
        const color = investor.hasCommitment ? 'success' : 'error';

        return (
          <Tooltip title={tooltip}>
            <span>
              {investor.hasCommitment ? (
                <StatusBadge color={color} />
              ) : (
                <EmptyStatus variant="h6" color="grey.500">
                  --
                </EmptyStatus>
              )}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const isTableLoading = (isFetching && !isRefetching) || isRemovingInvestor;
  const showSearchFilter =
    !!filters.search || investors.length > 0 || pagination.properties.page > 1;

  return (
    <Column spacing="xxl">
      <Grid container spacing={theme.spacingSizes.xxl}>
        <Grid size={{ xs: 12, md: 'grow' }}>
          <Column fullWidth spacing="sm">
            <TargetValue
              targetValue={fundraiseTarget}
              currentValue={signedCommitmentsValue}
              onSave={handleSaveTargetValue}
              isUpdatingOnboardingSettings={isUpdatingOnboardingSettings}
              baseCurrency={vehicle.baseCurrency}
            />

            <CommitmentsValueGraph
              vehicleCreateDate={vehicle.createdAt}
              baseCurrency={vehicle.baseCurrency}
              fundraiseTarget={fundraiseTarget}
              commitmentStats={commitmentStats}
            />
          </Column>
        </Grid>
        <Grid>
          <Divider orientation="vertical" />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Column fullWidth spacing="lg">
            <CommitmentsDetails
              signedCommitmentsValue={signedCommitmentsValue}
              prospectiveCommitmentsValue={prospectiveCommitmentsValue}
              targetCloseDate={targetCloseDate}
              handleChangeTargetCloseDate={handleChangeTargetCloseDate}
              vehicleCurrency={vehicle.baseCurrency}
              isUpdatingOnboardingSettings={isUpdatingOnboardingSettings}
            />
          </Column>
        </Grid>
      </Grid>

      <Column spacing="sm">
        <Typography variant="h5">Funnel progress</Typography>
        <PageFilters
          justify="space-between"
          actions={[
            { label: 'Add investor', onClick: addInvestorDisclosure.onOpen },
          ]}
        >
          {showSearchFilter ? (
            <Filter size="md">
              <SearchField
                clearable
                fullWidth
                placeholder="Search by investor"
                value={filters.search}
                autoSearchOnDebounce
                allowEmptyQuery
                onSearch={(query) => {
                  handleChangeFilters({ search: query });
                }}
              />
            </Filter>
          ) : null}
          {hasClosedCommitments ? (
            <Filter justify="flex-end">
              <Switch
                label="Show closed investors"
                onChange={(checked) => {
                  handleChangeFilters({ showClosed: checked });
                }}
                checked={filters.showClosed}
              />
            </Filter>
          ) : null}
          <Filter size="sm">
            <BasicSelect
              fullWidth
              value={sortSelectValue}
              onChange={(value) => {
                handleChangeSort(value);
              }}
              options={sortSelectOptions}
              placeholder="Sort by"
            />
          </Filter>
        </PageFilters>
        <Box>
          <Table
            columns={columns}
            rows={investors}
            isLoading={isTableLoading}
            rowsTotalCount={totalInvestors}
            pagination={pagination}
            disableSorting
            emptyMessage="No investors added"
            onRowClick={(investorApplication) => {
              goToVehicleApplication(
                investorApplication.id,
                TabType.InvestorDetails,
              );
            }}
            actions={[
              {
                label: 'View investor details',
                icon: RemoveRedEye,
                onClick: (investorApplication) => {
                  goToVehicleApplication(
                    investorApplication.id,
                    TabType.InvestorDetails,
                  );
                },
              },
              {
                label: 'Mark subscription complete',
                icon: CheckCircle,
                hidden: (investor) =>
                  investor.commitmentSettings.isManuallyAddedCommitment ||
                  investor.hasCommitment,
                onClick: (investorApplication) => {
                  goToVehicleApplication(
                    investorApplication.id,
                    TabType.InvestorJourney,
                    true,
                  );
                },
              },
              {
                label: 'Manage investor journey',
                icon: Settings,
                hidden: (investor) =>
                  investor.commitmentSettings.isManuallyAddedCommitment,
                onClick: (investorApplication) => {
                  goToVehicleApplication(
                    investorApplication.id,
                    TabType.InvestorJourney,
                  );
                },
              },
              {
                label: 'Manage communications',
                icon: Settings,
                onClick: (investorApplication) => {
                  goToVehicleApplication(
                    investorApplication.id,
                    TabType.Communications,
                  );
                },
              },
              {
                label: 'Delete commitment',
                icon: Delete,
                hidden: ({ commitmentId, hasClosedCommitment }) =>
                  !commitmentId || hasClosedCommitment,
                onClick: ({ commitmentId }) => {
                  if (!commitmentId) return;
                  deleteCommitmentsDisclosure.stageAction({ id: commitmentId });
                },
              },
              {
                label: 'Remove investor',
                icon: Delete,
                disabled: (investor) =>
                  investor.hasCommitment || isRemovingInvestor,
                tooltip: (investor) =>
                  investor.hasCommitment
                    ? 'This investor cannot be removed as their commitment has been created.'
                    : undefined,
                onClick: (investor) => {
                  removeInvestorDisclosure.stageAction({ id: investor.id });
                },
              },
            ]}
          />
          <ConfirmDialog
            open={deleteCommitmentsDisclosure.isOpen}
            onClose={deleteCommitmentsDisclosure.onClose}
            title="Delete commitment"
            content="Are you sure you want to delete this commitment?"
            actions={{
              confirm: {
                label: 'Delete commitment',
                color: 'error',
                onClick: () => {
                  deleteCommitmentsDisclosure.onClose();
                  handleDeleteCommitment(
                    deleteCommitmentsDisclosure.actionProps.id,
                  );
                },
              },
            }}
          />
          <ConfirmDialog
            open={removeInvestorDisclosure.isOpen}
            onClose={removeInvestorDisclosure.onClose}
            title="Remove investor"
            content="This will remove the investor from onboarding. Their data and record will remain on their Investor page."
            actions={{
              confirm: {
                label: 'Remove',
                color: 'error',
                onClick: () => {
                  removeInvestorDisclosure.onClose();
                  handleRemoveInvestor(removeInvestorDisclosure.actionProps.id);
                },
              },
            }}
          />
          <AddInvestorDrawer
            open={addInvestorDisclosure.isOpen}
            onClose={addInvestorDisclosure.onClose}
            onSuccess={(vehicleApplicationId, addWithCommitment) => {
              goToVehicleApplication(
                vehicleApplicationId,
                addWithCommitment
                  ? TabType.CommitmentDetails
                  : TabType.InvestorDetails,
              );
            }}
          />
          {!!selectedVehicleApplicationId && (
            <ErrorBoundary loader={<PageLoader />}>
              <ViewInvestor
                vehicle={vehicle}
                vehicleApplicationId={selectedVehicleApplicationId}
                onClose={() => {
                  closeVehicleApplication();
                  refetchInvestors();
                }}
              />
            </ErrorBoundary>
          )}
        </Box>
      </Column>
    </Column>
  );
};

const OverviewWrapper: React.FC = () => {
  const { vehicle } = useLayoutContext();

  if (!vehicle) {
    return null;
  }

  return (
    <OnboardingOverviewNavigationContextProvider>
      <Overview vehicle={vehicle} />
    </OnboardingOverviewNavigationContextProvider>
  );
};

export default OverviewWrapper;
